import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useFeatureToggles, useIntl } from '@grandvisionhq/state';
import { useCheckoutSettings } from '../../context/CheckoutSettings/context.js';
import '@grandvisionhq/utils';
import '@grandvisionhq/carts-v2';
import '../../StepShipping/selectors.js';
import 'lodash/isEmpty';
import '../../mappers/errors-mapping.js';
import { getCustomerDetailsPayload } from '../../utils/get-customer-details-payload.js';
import '../../models/cart/cart-state.js';
import '../../models/checkout/checkout-step-error-type.js';
import '../../models/checkout-settings.js';
import { ShippingMethod } from '../../models/step-shipping/shipping-methods.js';
import '../../models/shared.js';
import 'history';
import '../../utils/has-cart-errors.js';
import '@grandvisionhq/payments';
import '@grandvisionhq/auth';
import '../../selectors.js';
import { useSetCustomerDetails } from '../../hooks/operations/use-set-customer-details.js';
import '@grandvisionhq/graphql';
import '@grandvisionhq/lib-utils-cart';
import '@grandvisionhq/svc-tealium/events';
import '../../operations/mutations/set-customer-details.js';
import '../../operations/mutations/set-service-store.js';
import '../../operations/mutations/set-shipping-method.js';
import '../../operations/mutations/set-shipping-state.js';
import '../../operations/mutations/create-apple-pay-session.js';
import '../../operations/mutations/recreate-ordered-checkout.js';
import '@grandvisionhq/frk-routing';
import '@grandvisionhq/fun-orders';
import '@grandvisionhq/analytics';
import '../../operations/fragments/checkout-mutation-result.js';
import '../../operations/fragments/cart-mutation-failures.js';
import '../../operations/fragments/recreate-ordered-checkout-failure.js';
import '../../operations/fragments/recreate-ordered-checkout-result.js';
import '../../operations/fragments/paypal-payment.js';
import '../../operations/fragments/saved-user-details.js';
import '../../operations/queries/get-checkout-state.js';
import '../../operations/queries/get-user-details.js';
import { useLoggedInCheckout } from '../context/context.js';
import '@grandvisionhq/lib-utils-w3c';
import { isFormDataValid } from '../utils/validate-form-data.js';

const useValidateAndSetOrderDetails = (props)=>{
    const { formFieldsMap , features , serviceStore , consent: consentSettings  } = useCheckoutSettings();
    const isServiceStoreEnabled = features === null || features === void 0 ? void 0 : features.isServiceStoreEnabled;
    const { isMultilingualEnabled  } = useFeatureToggles();
    const intl = useIntl();
    const { userDetailsState , shippingState , isCompanyOrder , setisOrderDetailsValid , setIsOrderInProgress , updateConsents , isAddressesEqual ,  } = useLoggedInCheckout();
    const { pickupPoint , store  } = React.useMemo(()=>shippingState
    , [
        shippingState
    ]);
    const isServiceStoreValid = React.useCallback((selectedShippingMethod)=>{
        return Boolean(selectedShippingMethod.method == ShippingMethod.Pickup && store) || Boolean(!isServiceStoreEnabled || shippingState.serviceStore || (serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.isOptional));
    }, [
        isServiceStoreEnabled,
        serviceStore === null || serviceStore === void 0 ? void 0 : serviceStore.isOptional,
        shippingState.serviceStore,
        store
    ]);
    const isShippingMethodValid = React.useCallback((selectedShippingMethod)=>{
        if (!selectedShippingMethod.method) return false;
        if (selectedShippingMethod.method == ShippingMethod.PickupPoint && !pickupPoint) return false;
        if (selectedShippingMethod.method == ShippingMethod.Pickup && !store) return false;
        return selectedShippingMethod.isValid;
    }, [
        pickupPoint,
        store
    ]);
    const areCustomerDetailsValid = React.useCallback(()=>{
        if (formFieldsMap) {
            return Object.entries(formFieldsMap).every(([formName, formFieldItems])=>{
                if (!isCompanyOrder && formName == 'company') return true;
                if (isCompanyOrder && formName == 'invoice') return true;
                if (formName == 'deliveryAddress' && isAddressesEqual) return true;
                if (formName == 'deliveryAddress' && !userDetailsState[formName] && userDetailsState.billingAddress) {
                    return true;
                }
                const isValid = isFormDataValid(formFieldItems, userDetailsState[formName]);
                return isValid;
            });
        } else {
            return false;
        }
    }, [
        isAddressesEqual,
        formFieldsMap,
        isCompanyOrder,
        userDetailsState
    ]);
    const isConsentGiven = React.useCallback((consents)=>{
        const consentsValid = (consentSettings === null || consentSettings === void 0 ? void 0 : consentSettings.implicitTermsAndConditions) ? true : Boolean(consents && !consents.some((consent)=>!consent.isChecked
        ));
        if (!consentsValid) {
            updateConsents(consents.map((consent)=>swcHelpers.objectSpread({}, consent, {
                    shouldValidate: !!consent.required
                })
            ));
        }
        return consentsValid;
    }, [
        consentSettings === null || consentSettings === void 0 ? void 0 : consentSettings.implicitTermsAndConditions,
        updateConsents
    ]);
    const isOrderDetailsValid = React.useCallback((selectedShippingMethod, consents)=>{
        return isConsentGiven(consents || []) && isShippingMethodValid(selectedShippingMethod) && areCustomerDetailsValid() && isServiceStoreValid(selectedShippingMethod);
    }, [
        areCustomerDetailsValid,
        isConsentGiven,
        isServiceStoreValid,
        isShippingMethodValid
    ]);
    const { setCustomerDetails , updateError , isUpdating  } = useSetCustomerDetails(swcHelpers.objectSpread({}, (props === null || props === void 0 ? void 0 : props.onComplete) ? {
        onCompleted: ()=>props.onComplete()
    } : {}, {
        onError: ()=>{
            toast.failed('checkout.errors.form.general');
        }
    }));
    const handleOrderDetailsValidation = React.useCallback((selectedShippingMethod, consents)=>{
        if (userDetailsState.billingAddress && isOrderDetailsValid(selectedShippingMethod, consents)) {
            setisOrderDetailsValid(true);
            return true;
        } else {
            return false;
        }
    }, [
        isOrderDetailsValid,
        setisOrderDetailsValid,
        userDetailsState.billingAddress
    ]);
    const saveCustomerInformationWithoutValidation = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        const { billingAddress , deliveryAddress , contactDetails , invoice , company  } = userDetailsState;
        const shippingDetails = {
            billingAddress,
            deliveryAddress,
            contactDetails,
            invoice,
            company,
            expressCheckout: undefined
        };
        var _pickupPoint, _store;
        const customerDetaislPayload = getCustomerDetailsPayload({
            shippingDetails,
            opcoCountry: intl.country,
            locale: intl.locale,
            isMultilingualEnabled,
            selectedPickupPoint: (_pickupPoint = shippingState.pickupPoint) !== null && _pickupPoint !== void 0 ? _pickupPoint : undefined,
            selectedShippingMethod: shippingState.selectedShippingMethod.method,
            selectedStore: (_store = shippingState.store) !== null && _store !== void 0 ? _store : undefined
        });
        return yield setCustomerDetails(customerDetaislPayload);
    }), [
        intl.country,
        intl.locale,
        isMultilingualEnabled,
        setCustomerDetails,
        shippingState.pickupPoint,
        shippingState.selectedShippingMethod.method,
        shippingState.store,
        userDetailsState, 
    ]);
    const saveCustomerInformation = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(selectedShippingMethod, consents) {
            if (userDetailsState.billingAddress && isOrderDetailsValid(selectedShippingMethod, consents)) {
                setisOrderDetailsValid(true);
                const { billingAddress , deliveryAddress , contactDetails , invoice , company  } = userDetailsState;
                const shippingDetails = {
                    billingAddress,
                    deliveryAddress,
                    contactDetails,
                    invoice,
                    company,
                    expressCheckout: undefined
                };
                var _pickupPoint, _store;
                const customerDetaislPayload = getCustomerDetailsPayload({
                    shippingDetails,
                    opcoCountry: intl.country,
                    locale: intl.locale,
                    isMultilingualEnabled,
                    selectedPickupPoint: (_pickupPoint = shippingState.pickupPoint) !== null && _pickupPoint !== void 0 ? _pickupPoint : undefined,
                    selectedShippingMethod: shippingState.selectedShippingMethod.method,
                    selectedStore: (_store = shippingState.store) !== null && _store !== void 0 ? _store : undefined
                });
                return yield setCustomerDetails(customerDetaislPayload);
            } else {
                setisOrderDetailsValid(false);
                setIsOrderInProgress(false);
                toast.failed('checkout.errors.form.general');
                throw Error('checkout.errors.form.general');
            }
        });
        return function(selectedShippingMethod, consents) {
            return _ref.apply(this, arguments);
        };
    }(), [
        isOrderDetailsValid,
        intl.country,
        intl.locale,
        isMultilingualEnabled,
        setisOrderDetailsValid,
        setCustomerDetails,
        setIsOrderInProgress,
        shippingState.pickupPoint,
        shippingState.selectedShippingMethod,
        shippingState.store,
        userDetailsState, 
    ]);
    return React.useMemo(()=>({
            saveCustomerInformation,
            saveCustomerInformationWithoutValidation,
            handleOrderDetailsValidation,
            isUpdating,
            updateError: Boolean(updateError)
        })
    , [
        isUpdating,
        saveCustomerInformation,
        updateError,
        saveCustomerInformationWithoutValidation,
        handleOrderDetailsValidation, 
    ]);
};

export { useValidateAndSetOrderDetails };
//# sourceMappingURL=useValidateAndSetOrderDetails.js.map
